import {observer} from 'mobx-react-lite'
import React, {useEffect, useRef, useState} from 'react'
import tw from 'tailwind-styled-components'
import {
  GroupChat,
  Hub,
  newsCompanyId,
  repeatTemplatesContainerId
} from 'yougile-model'
import {Sync} from '@client/sync'
import {LocationDependentChatHeader} from '@components/chat/chat-header'
import {crmStore} from '@components/crm/crm-store'
import {FileViewer} from '@components/file-viewer/file-viewer'
import {AboutClientPanel} from '@components/right-panel/crm/about-client/about-client-panel'
import {DealHistory} from '@components/right-panel/crm/deal-history/deal-history'
import {DefaultFieldsPanel} from '@components/right-panel/crm/default-fields/default-fields-panel'
import {useSyncEvent} from '@containers/hooks'
import {cn} from '@containers/html-utils'
import {ChatUtils} from '@extensions/chat-utils'
import {Feedback} from '@extensions/feedback'
import {Chat} from '../chat'
import {ChatContext, ChatStore} from '../chat/chat-store'
import {ChecklistsTab} from '../checklists-tab/checklists-tab'
import {rightPanelStore} from '../right-panel'
import {TaskDescription} from '../task-description'
import {TaskInfoLog} from '../task-info-log/task-info-log'
import {
  RightPanelContentContext,
  RightPanelContentStore,
  useRightPanelContentStore
} from './right-panel-content-store'

const TwHeader = tw.div<{$newBg?: boolean}>`
  flex-none
  shadow-border-b-1
  shadow-grey-l60
  ${p => (p.$newBg ? 'bg-background-primary' : 'bg-grey-l100')}
`

const TwChatItem = tw.div`
  chat-item
  h-full
  w-full
  flex
  flex-col
  overflow-hidden
`

type Props = {
  hub: Hub | undefined
  forceHeight?: number
}

const RightPanelContentBase = observer((props: Props) => {
  const {hub, forceHeight} = props
  const headerRef = useRef<HTMLDivElement>()
  const [headerHeight, setHeaderHeight] = useState(
    forceHeight ? forceHeight : 0
  )
  const [panelState, panelStore] = useRightPanelContentStore()

  useEffect(() => {
    if (!forceHeight) {
      setHeaderHeight(headerRef.current?.offsetHeight ?? 0)
    }
  }, [headerRef.current?.offsetHeight, panelState.chatPinModified])

  useEffect(() => {
    panelStore.setDescriptionScroll(-1)
    panelStore.setSubtasksScroll(-1)
    rightPanelStore.syncTabByHub(hub)
  }, [hub?.id])

  const isGroupChat = !!hub && GroupChat.isGroupChat(hub)
  const isNews = hub?.companyId === newsCompanyId
  const isDirect =
    !!hub &&
    hub.companyId === Sync.profileId &&
    Sync.getProfile().getProjectFor(hub)?.title === 'Direct'
  const isFeedbackTicket =
    !!hub && !!Feedback.column && hub.location === Feedback.column.id
  const isRepeatTemplate = !!hub && hub.location === repeatTemplatesContainerId
  const isCrmDefaultFieldsHub = ['contact', 'organization'].includes(
    crmStore.getCrmRoleOfHub(hub) ?? ''
  )
  const noTabs =
    isGroupChat ||
    isNews ||
    isDirect ||
    isFeedbackTicket ||
    isCrmDefaultFieldsHub
  const {autofocus} = rightPanelStore.state
  const currentTab = rightPanelStore.currentTab
  const showDescription =
    !noTabs &&
    (currentTab === 'description' ||
      (currentTab === 'chat' && isRepeatTemplate))

  const showConnectedTasks = !noTabs && currentTab === 'connected'
  const showDealHistory = !noTabs && currentTab === 'dealHistory'
  const showAboutClient = !noTabs && currentTab === 'aboutClient'
  const showChecklistsTab =
    !noTabs && currentTab === 'checklists' && !isNews && !isFeedbackTicket
  const isChat = noTabs || (!isRepeatTemplate && currentTab === 'chat')
  const classNames = cn(
    'chat-item-cnt',
    'h-full',
    (isDirect || isFeedbackTicket || isGroupChat) && 'direct',
    isNews && 'news'
  )

  useSyncEvent(
    'current',
    {debounceTime: 1000, filter: hub?.id},
    () => {
      if (hub && !isNews && (hub.deleted || Sync.get(hub).isDeleted(hub.id))) {
        ChatUtils.isChatExpanded() && ChatUtils.openTaskChats()
      }
    },
    [hub]
  )

  const newBg = false

  return (
    <div className={classNames} tabIndex={-1}>
      <TwChatItem>
        <TwHeader ref={headerRef} $newBg={newBg}>
          <LocationDependentChatHeader />
        </TwHeader>

        {isChat && !isCrmDefaultFieldsHub && (
          <Chat
            hub={hub}
            noTabs={noTabs}
            headerHeight={headerHeight}
            autofocus={autofocus}
          />
        )}

        {showConnectedTasks && hub && <TaskInfoLog hub={hub} />}

        {showDealHistory && hub && (
          <ChatContext.Provider value={new ChatStore(hub, true)}>
            <DealHistory hub={hub} />
          </ChatContext.Provider>
        )}

        {showAboutClient && hub && <AboutClientPanel deal={hub} />}
        <div
          style={{display: showDescription ? 'block' : 'none'}}
          className="h-full"
        >
          <TaskDescription
            hub={hub}
            headerHeight={headerHeight}
            newBg={newBg}
          />
        </div>

        {isCrmDefaultFieldsHub && <DefaultFieldsPanel deal={hub} />}

        {showChecklistsTab && <ChecklistsTab hub={hub} newBg={newBg} />}

        {!!panelState.selectedFilesToView.length && (
          <FileViewer
            autoplay
            files={panelState.selectedFilesToView}
            selectedIndex={panelState.selectedFileToViewIndex}
            onClose={() => panelStore.closeFileViewer()}
            hub={hub}
          />
        )}
      </TwChatItem>
    </div>
  )
})

export const RightPanelContent = (props: Props) => {
  const [rightPanelContentStore] = useState(() => new RightPanelContentStore())
  return (
    <RightPanelContentContext.Provider value={rightPanelContentStore}>
      <RightPanelContentBase {...props} />
    </RightPanelContentContext.Provider>
  )
}
